import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private readonly refreshedToken$ = new BehaviorSubject<string | null>(null);

  constructor(private auth: AuthService) { }

  private toAbsoluteApiUrl(pathOrUrl: string): string {
    if (!pathOrUrl) {
      return environment.apiBaseUrl;
    }

    if (/^https?:\/\//i.test(pathOrUrl)) {
      return pathOrUrl;
    }

    const baseUrl = (environment.apiBaseUrl || '').replace(/\/$/, '');
    const endpoint = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${baseUrl}${endpoint}`;
  }

  private isAuthPublicEndpoint(url: string): boolean {
    const normalizedUrl = url.toLowerCase();
    const refreshUrl = this.toAbsoluteApiUrl(environment.auth.refreshEndpoint).toLowerCase();
    const revokeUrl = this.toAbsoluteApiUrl(environment.auth.revokeEndpoint).toLowerCase();

    return normalizedUrl.includes('/api/admin/login') ||
      normalizedUrl.includes('/api/store/storelogin') ||
      normalizedUrl.includes('/api/delivery/login') ||
      normalizedUrl.includes('/api/user/login') ||
      normalizedUrl.startsWith(refreshUrl) ||
      normalizedUrl.startsWith(revokeUrl) ||
      normalizedUrl.includes(environment.auth.refreshEndpoint.toLowerCase()) ||
      normalizedUrl.includes(environment.auth.revokeEndpoint.toLowerCase());
  }

  private decodeJwtExpirationMs(token: string): number | null {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }

    try {
      const normalizedPayload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(normalizedPayload));
      const exp = Number(payload?.exp);

      if (!Number.isFinite(exp)) {
        return null;
      }

      return exp * 1000;
    } catch {
      return null;
    }
  }

  private shouldRefreshAdminToken(token: string): boolean {
    const expirationMs = this.decodeJwtExpirationMs(token);

    if (!expirationMs) {
      return false;
    }

    return Date.now() + environment.auth.refreshSkewMs >= expirationMs;
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleRefreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshingToken) {
      return this.refreshedToken$.pipe(
        filter((token): token is string => !!token),
        take(1),
        switchMap(token => next.handle(this.addAuthHeader(req, token)))
      );
    }

    this.isRefreshingToken = true;
    this.refreshedToken$.next(null);

    return this.auth.refreshAdminToken().pipe(
      switchMap((token) => {
        if (!token) {
          this.auth.logout();
          return throwError('Unable to refresh token');
        }

        this.refreshedToken$.next(token);
        return next.handle(this.addAuthHeader(req, token));
      }),
      catchError((refreshError) => {
        this.auth.logout();
        return throwError(refreshError);
      }),
      finalize(() => {
        this.isRefreshingToken = false;
      })
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl =
      req.url.startsWith(environment.apiBaseUrl) ||
      req.url.startsWith('/api/') ||
      req.url.includes('/api/');
    const isStoreRoute = /\/api\/Store(\/|$|\?)/i.test(req.url);
    const isAuthPublicRequest = this.isAuthPublicEndpoint(req.url);

    if (!isApiUrl || isAuthPublicRequest) {
      return next.handle(req);
    }

    const preferredToken = isStoreRoute ? this.auth.getStoreToken() : this.auth.getToken();
    const fallbackToken = isStoreRoute ? this.auth.getToken() : this.auth.getStoreToken();
    const token = preferredToken || fallbackToken;
    const shouldUseAdminRefresh = (!preferredToken && !!fallbackToken) || !isStoreRoute;

    if (token && shouldUseAdminRefresh && this.shouldRefreshAdminToken(token) && this.auth.getAdminRefreshToken()) {
      return this.handleRefreshAndRetry(req, next);
    }

    const requestWithAuth = token ? this.addAuthHeader(req, token) : req;

    return next.handle(requestWithAuth).pipe(
      catchError((error: HttpErrorResponse) => {
        const alreadyRetried = req.headers.has('x-auth-refresh-retried');
        if (error.status !== 401 || alreadyRetried || !this.auth.getAdminRefreshToken()) {
          return throwError(error);
        }

        const retryRequest = req.clone({
          setHeaders: {
            'x-auth-refresh-retried': 'true',
          },
        });

        return this.handleRefreshAndRetry(retryRequest, next);
      })
    );
  }
}
